import React from 'react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import SimpleBar from 'simplebar-react'
import 'simplebar-react/dist/simplebar.min.css'

import { CBadge, CNavLink, CSidebarNav } from '@coreui/react'
import { useTranslationContext } from '@/context/TranslationContext'

/**
 * @name AppSidebarNav
 * @description A sidebar navigation component that renders a list of navigation items, groups, and badges. It handles rendering of both individual items and nested groups of items, as well as managing their visual styles like indentation and badge display.
 * @param {Array} items - An array of navigation items or groups, each potentially containing sub-items.
 * @returns {JSX.Element} The rendered sidebar navigation with nested items and badges.
 */
export const AppSidebarNav = ({ items }) => {
  const t = useTranslationContext();
  
  const navLink = (name, icon, badge, indent = false) => {
    return (
      <>
        {icon
          ? icon
          : indent && (
            <span className="nav-icon">
              <span className="nav-icon-bullet"></span>
            </span>
          )}
        <div className='text-wrap text-break w-100'>{t(name)}</div>
        {badge && (
          <CBadge color={badge.color} className="ms-auto" size="sm">
            {badge.text}
          </CBadge>
        )}
      </>
    )
  }

  /**
   * @name navItem
   * @description A helper function to render an individual navigation item.
   * @param {Object} item - The navigation item object containing properties like name, badge, icon, and link.
   * @param {number} index - The index of the navigation item in the list.
   * @param {boolean} indent - A flag to indicate if the item should be indented.
   * @returns {JSX.Element} The rendered navigation item.
   */
  const navItem = (item, index, indent = false) => {
    const { component, name, badge, icon, ...rest } = item
    const Component = component
    // Debug: Check if rest contains a key prop
    console.log('navItem - rest contains key?:', 'key' in rest, 'key value:', rest.key)
    return (
      <Component as="div" key={index}>
        {rest.to || rest.href ? (
          <CNavLink
            {...(rest.href && { as: Link })}
            component={Link}
            to={rest.to || rest.href}
            {...rest}
          >
            {navLink(name, icon, badge, indent)}
          </CNavLink>
        ) : (
          navLink(name, icon, badge, indent)
        )}
      </Component>
    )
  }

  /**
   * @name navGroup
   * @description A helper function to render a group of navigation items, potentially containing nested items.
   * @param {Object} item - The group item containing a list of sub-items.
   * @param {number} index - The index of the navigation group.
   * @returns {JSX.Element} The rendered group of navigation items.
   */
  const navGroup = (item, index) => {
    const { component, name, icon, items, to, ...rest } = item
    const Component = component
    return (
      <Component compact as="div" key={index} toggler={navLink(name, icon)} {...rest}>
        {items?.map((item, index) =>
          item.items ? navGroup(item, index) : navItem(item, index, true),
        )}
      </Component>
    )
  }

  return (
    <CSidebarNav as={SimpleBar}>
      {items &&
        items.map((item, index) => (item.items ? navGroup(item, index) : navItem(item, index)))}
    </CSidebarNav>
  )
}

/**
 * @name propTypes
 * @description Defines the expected prop types for the AppSidebarNav component.
 * @param {Array} items - The array of navigation items or groups to render.
 */
AppSidebarNav.propTypes = {
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
}
